import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Container, Row } from "reactstrap";
import {
  Typography,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faHandPointer, faFlag, faClockRotateLeft } from "@fortawesome/pro-duotone-svg-icons";
import ErrorBoundary from "../../ErrorBoundry";
import {
  getUnmappedPositions,
  getUnmatchedClassifications,
  getPositionsWithNoClassification,
  getClassificationsLookup,
  proposePositionMapping,
  getMyMappingHistory,
} from "../../store/positionMapping/actions";
import { getLookupPositions } from "../../store/position/actions";

const renderValue = (value) =>
  value !== null && value !== undefined && value !== "" ? value : "N/A";

function UnmappedPositions() {
  const dispatch = useDispatch();
  const {
    unmappedList,
    loadingUnmapped,
    unmatchedList,
    loadingUnmatched,
    noClassificationList,
    loadingNoClassification,
    classificationsLookupList,
    myHistoryList,
    loadingMyHistory,
  } = useSelector((state) => state.PositionMappingReducer);
  const { lookupPositionList } = useSelector((state) => state.PositionReducer);
  const [proposingKey, setProposingKey] = useState(null);
  const [manualSelection, setManualSelection] = useState({});
  const [classificationSelection, setClassificationSelection] = useState({});
  const [filter, setFilter] = useState("matched");
  // Only ONE picker is ever open/mounted at a time - rendering a fully-bound
  // Autocomplete per table row (against potentially large lookup lists) was
  // freezing the page with many rows. A single shared dialog fixes that.
  const [picker, setPicker] = useState(null); // { type: "position" | "classification", key }
  const [matchedSearch, setMatchedSearch] = useState("");
  const [unmatchedSearch, setUnmatchedSearch] = useState("");
  const [noClassificationSearch, setNoClassificationSearch] = useState("");

  const filteredUnmappedList = useMemo(() => {
    const query = matchedSearch.trim().toLowerCase();
    if (!query) return unmappedList;
    return unmappedList.filter((row) =>
      `${row.jobClassificationCode} ${row.payrollPosition || ""}`.toLowerCase().includes(query)
    );
  }, [unmappedList, matchedSearch]);

  const filteredUnmatchedList = useMemo(() => {
    const query = unmatchedSearch.trim().toLowerCase();
    if (!query) return unmatchedList;
    return unmatchedList.filter((row) =>
      `${row.jobClassificationCode} ${row.payrollPosition || ""}`.toLowerCase().includes(query)
    );
  }, [unmatchedList, unmatchedSearch]);

  const filteredNoClassificationList = useMemo(() => {
    const query = noClassificationSearch.trim().toLowerCase();
    if (!query) return noClassificationList;
    return noClassificationList.filter((row) =>
      `${row.npositionNo} ${row.hrPosition || ""}`.toLowerCase().includes(query)
    );
  }, [noClassificationList, noClassificationSearch]);

  useEffect(() => {
    dispatch(getUnmappedPositions());
    dispatch(getUnmatchedClassifications());
    dispatch(getPositionsWithNoClassification());
    dispatch(getClassificationsLookup());
    dispatch(getLookupPositions());
    dispatch(getMyMappingHistory());
  }, [dispatch]);

  const handlePropose = (row) => {
    const key = `${row.npositionNo}-${row.jobClassificationCode}`;
    setProposingKey(key);
    dispatch(
      proposePositionMapping({
        npositionNo: row.npositionNo,
        jobClassificationCode: row.jobClassificationCode,
      })
    );
  };

  const handleManualSubmit = (row) => {
    const selected = manualSelection[row.jobClassificationCode];
    if (!selected) return;
    setProposingKey(`manual-${row.jobClassificationCode}`);
    dispatch(
      proposePositionMapping({
        npositionNo: selected.npositionNo,
        jobClassificationCode: row.jobClassificationCode,
      })
    );
  };

  const handleFlagNewClassification = (row) => {
    const key = `flag-${row.npositionNo}`;
    const found = classificationSelection[row.npositionNo];
    setProposingKey(key);
    dispatch(
      found
        ? proposePositionMapping({
            npositionNo: row.npositionNo,
            jobClassificationCode: found.jobClassificationCode,
          })
        : proposePositionMapping({
            npositionNo: row.npositionNo,
            requestType: "NewClassification",
          })
    );
  };

  const handlePickerSelect = (value) => {
    if (!picker) return;
    if (picker.type === "position") {
      setManualSelection((prev) => ({ ...prev, [picker.key]: value }));
    } else {
      setClassificationSelection((prev) => ({ ...prev, [picker.key]: value }));
    }
    setPicker(null);
  };

  return (
    <ErrorBoundary>
      <Fragment>
        <Container fluid={true}>
          <Row className="bg-primary rounded py-2 mb-3 sticky-top">
            <Col className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faLink} size="xl" color="white" />
              <Typography variant="h6" style={{ color: "white" }}>
                Unmapped HR / Payroll Positions
              </Typography>
            </Col>
          </Row>

          <Card>
            <CardContent style={{ paddingBottom: 8 }}>
              <RadioGroup
                row
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <FormControlLabel
                  value="matched"
                  control={<Radio size="small" />}
                  label={`Suggested Matches (${unmappedList.length})`}
                />
                <FormControlLabel
                  value="unmatched"
                  control={<Radio size="small" />}
                  label={`No Suggested Match (${unmatchedList.length})`}
                />
                <FormControlLabel
                  value="noClassification"
                  control={<Radio size="small" />}
                  label={`No Payroll Classification At All (${noClassificationList.length})`}
                />
                <FormControlLabel
                  value="myHistory"
                  control={<Radio size="small" />}
                  label={`My Submitted Requests (${myHistoryList.length})`}
                />
              </RadioGroup>
            </CardContent>

            {filter === "matched" && (
              <Fragment>
                <CardHeader
                  title="Suggested Matches Pending Confirmation"
                  subheader="Propose a mapping below to send it to Payroll for approval."
                  style={{ paddingTop: 0 }}
                />
                <CardContent style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  {loadingUnmapped ? (
                    <Typography variant="body2" className="p-3 text-center">
                      Loading...
                    </Typography>
                  ) : unmappedList.length === 0 ? (
                    <Typography variant="body2" className="p-3 text-center">
                      No unmapped positions found.
                    </Typography>
                  ) : (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Match Method</TableCell>
                          <TableCell>HR Position</TableCell>
                          <TableCell style={{ minWidth: 220 }}>
                            Payroll Job Classification
                            <TextField
                              size="small"
                              variant="standard"
                              fullWidth
                              placeholder="Search..."
                              value={matchedSearch}
                              onChange={(e) => setMatchedSearch(e.target.value)}
                              sx={{ mt: 0.5 }}
                            />
                          </TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredUnmappedList.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" className="p-2">
                                No rows match "{matchedSearch}".
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {filteredUnmappedList.map((row, idx) => {
                          const key = `${row.npositionNo}-${row.jobClassificationCode}`;
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <Chip size="small" label={renderValue(row.matchMethod)} />
                              </TableCell>
                              <TableCell>
                                {renderValue(row.npositionNo)} - {renderValue(row.hrPosition)}
                              </TableCell>
                              <TableCell>
                                {renderValue(row.jobClassificationCode)} -{" "}
                                {renderValue(row.payrollPosition)}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={proposingKey === key}
                                  onClick={() => handlePropose(row)}
                                >
                                  Propose Mapping
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Fragment>
            )}

            {filter === "unmatched" && (
              <Fragment>
                <CardHeader
                  title="Job Classifications With No Suggested Match"
                  subheader="Neither auto-match found a candidate for these - pick the correct HR position manually and submit for Payroll approval."
                  avatar={<FontAwesomeIcon icon={faHandPointer} />}
                  style={{ paddingTop: 0 }}
                />
                <CardContent style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  {loadingUnmatched ? (
                    <Typography variant="body2" className="p-3 text-center">
                      Loading...
                    </Typography>
                  ) : unmatchedList.length === 0 ? (
                    <Typography variant="body2" className="p-3 text-center">
                      No unmatched job classifications found.
                    </Typography>
                  ) : (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ minWidth: 220 }}>
                            Payroll Job Classification
                            <TextField
                              size="small"
                              variant="standard"
                              fullWidth
                              placeholder="Search..."
                              value={unmatchedSearch}
                              onChange={(e) => setUnmatchedSearch(e.target.value)}
                              sx={{ mt: 0.5 }}
                            />
                          </TableCell>
                          <TableCell style={{ minWidth: 280 }}>HR Position</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredUnmatchedList.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Typography variant="body2" className="p-2">
                                No rows match "{unmatchedSearch}".
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {filteredUnmatchedList.map((row, idx) => {
                          const manualKey = `manual-${row.jobClassificationCode}`;
                          const selected = manualSelection[row.jobClassificationCode];
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                {renderValue(row.jobClassificationCode)} -{" "}
                                {renderValue(row.payrollPosition)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    setPicker({
                                      type: "position",
                                      key: row.jobClassificationCode,
                                      context: `${renderValue(row.jobClassificationCode)} - ${renderValue(
                                        row.payrollPosition
                                      )}`,
                                    })
                                  }
                                >
                                  {selected
                                    ? `${selected.npositionNo} - ${selected.title || ""}`
                                    : "Select HR Position..."}
                                </Button>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!selected || proposingKey === manualKey}
                                  onClick={() => handleManualSubmit(row)}
                                >
                                  Submit
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Fragment>
            )}

            {filter === "noClassification" && (
              <Fragment>
                <CardHeader
                  title="HR Positions With No Payroll Classification At All"
                  subheader="Title matching can miss real matches spelled or abbreviated differently - search the full classification list first. Only flag it for Payroll if you genuinely can't find one."
                  avatar={<FontAwesomeIcon icon={faFlag} />}
                  style={{ paddingTop: 0 }}
                />
                <CardContent style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  {loadingNoClassification ? (
                    <Typography variant="body2" className="p-3 text-center">
                      Loading...
                    </Typography>
                  ) : noClassificationList.length === 0 ? (
                    <Typography variant="body2" className="p-3 text-center">
                      No positions found.
                    </Typography>
                  ) : (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ minWidth: 220 }}>
                            HR Position
                            <TextField
                              size="small"
                              variant="standard"
                              fullWidth
                              placeholder="Search..."
                              value={noClassificationSearch}
                              onChange={(e) => setNoClassificationSearch(e.target.value)}
                              sx={{ mt: 0.5 }}
                            />
                          </TableCell>
                          <TableCell style={{ minWidth: 280 }}>
                            Search Existing Classifications (optional)
                          </TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredNoClassificationList.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Typography variant="body2" className="p-2">
                                No rows match "{noClassificationSearch}".
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {filteredNoClassificationList.map((row, idx) => {
                          const key = `flag-${row.npositionNo}`;
                          const found = classificationSelection[row.npositionNo];
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                {renderValue(row.npositionNo)} - {renderValue(row.hrPosition)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    setPicker({
                                      type: "classification",
                                      key: row.npositionNo,
                                      context: `${renderValue(row.npositionNo)} - ${renderValue(
                                        row.hrPosition
                                      )}`,
                                    })
                                  }
                                >
                                  {found
                                    ? `${found.jobClassificationCode} - ${
                                        found.jobClassificationTitle || ""
                                      }`
                                    : "Search Classifications..."}
                                </Button>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="contained"
                                  color={found ? "primary" : "warning"}
                                  disabled={proposingKey === key}
                                  onClick={() => handleFlagNewClassification(row)}
                                >
                                  {found ? "Submit as Mapping" : "Flag for Payroll"}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Fragment>
            )}

            {filter === "myHistory" && (
              <Fragment>
                <CardHeader
                  title="My Submitted Requests"
                  subheader="Everything you've proposed or flagged, and its current status with Payroll."
                  avatar={<FontAwesomeIcon icon={faClockRotateLeft} />}
                  style={{ paddingTop: 0 }}
                />
                <CardContent style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  {loadingMyHistory ? (
                    <Typography variant="body2" className="p-3 text-center">
                      Loading...
                    </Typography>
                  ) : myHistoryList.length === 0 ? (
                    <Typography variant="body2" className="p-3 text-center">
                      You haven't submitted any requests yet.
                    </Typography>
                  ) : (
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>HR Position</TableCell>
                          <TableCell>Payroll Job Classification</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Proposed On</TableCell>
                          <TableCell>Remarks</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {myHistoryList.map((row) => {
                          const isNewClassification = row.requestType === "NewClassification";
                          const statusColor =
                            row.status === "Approved"
                              ? "success"
                              : row.status === "Rejected"
                              ? "error"
                              : "warning";
                          return (
                            <TableRow key={row.id}>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={isNewClassification ? "Needs New Classification" : "Mapping"}
                                />
                              </TableCell>
                              <TableCell>
                                {renderValue(row.npositionNo)} - {renderValue(row.hrPositionTitle)}
                              </TableCell>
                              <TableCell>
                                {isNewClassification
                                  ? "— (none yet)"
                                  : `${renderValue(row.jobClassificationCode)} - ${renderValue(
                                      row.payrollPositionTitle
                                    )}`}
                              </TableCell>
                              <TableCell>
                                <Chip size="small" color={statusColor} label={row.status} />
                              </TableCell>
                              <TableCell>
                                {row.proposedDate ? new Date(row.proposedDate).toLocaleString() : "N/A"}
                              </TableCell>
                              <TableCell>{renderValue(row.remarks)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Fragment>
            )}
          </Card>
        </Container>

        <Dialog open={!!picker} onClose={() => setPicker(null)} fullWidth maxWidth="sm">
          <DialogTitle>
            {picker?.type === "position" ? "Select HR Position" : "Search Job Classifications"}
          </DialogTitle>
          <DialogContent>
            {picker && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                For {picker.type === "position" ? "payroll classification" : "HR position"}:{" "}
                <strong>{picker.context}</strong>
              </Typography>
            )}
            {picker && (
              <Autocomplete
                autoFocus
                options={
                  picker.type === "position"
                    ? lookupPositionList || []
                    : classificationsLookupList || []
                }
                getOptionLabel={(option) =>
                  picker.type === "position"
                    ? `${option.npositionNo} - ${option.title || ""}`
                    : `${option.jobClassificationCode} - ${option.jobClassificationTitle || ""}`
                }
                isOptionEqualToValue={(option, value) =>
                  picker.type === "position"
                    ? option.npositionNo === value?.npositionNo
                    : option.jobClassificationCode === value?.jobClassificationCode
                }
                // Don't dump the entire list open on focus - nothing shows until you type
                // at least 2 characters, and results are capped so it's always a short,
                // targeted list instead of something to scroll through.
                filterOptions={(options, { inputValue }) => {
                  const query = inputValue.trim().toLowerCase();
                  if (query.length < 2) return [];
                  return options
                    .filter((option) =>
                      (picker.type === "position"
                        ? `${option.npositionNo} ${option.title || ""}`
                        : `${option.jobClassificationCode} ${option.jobClassificationTitle || ""}`
                      )
                        .toLowerCase()
                        .includes(query)
                    )
                    .slice(0, 50);
                }}
                noOptionsText="Type at least 2 characters to search..."
                onChange={(event, newValue) => handlePickerSelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    placeholder={
                      picker.type === "position"
                        ? "Type a position title or number..."
                        : "Type a classification title or code..."
                    }
                  />
                )}
              />
            )}
          </DialogContent>
        </Dialog>
      </Fragment>
    </ErrorBoundary>
  );
}

export default UnmappedPositions;
